import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeftRight } from 'lucide-react';
import { parseAmount, sanitizeAmount } from '@utils/amountInput';

interface MarketDataLike {
  symbol: string;
  price: number;
}

const CURRENCIES = [
  { code: 'TRY', name: 'Türk Lirası' },
  { code: 'USD', name: 'Amerikan Doları' },
  { code: 'EUR', name: 'Euro' },
  { code: 'GBP', name: 'İngiliz Sterlini' },
  { code: 'CHF', name: 'İsviçre Frangı' },
];

const CurrencyConverter: React.FC<{ marketData: MarketDataLike[]; hideFullPageLink?: boolean }> = ({ marketData, hideFullPageLink = false }) => {
  // String, not number: a number cannot hold an empty box.
  const [amount, setAmount] = useState('1');
  const [fromCurrency, setFromCurrency] = useState<string>('USD');
  const [toCurrency, setToCurrency] = useState<string>('TRY');

  const getRate = (from: string, to: string): number | null => {
    if (from === to) return 1;
    const rates: Record<string, number> = { TRY: 1 };
    const find = (s: string) => marketData.find(d => d.symbol === s);
    const usdtry = find('USDTRY=X'); if (usdtry) rates['USD'] = usdtry.price;
    const eurtry = find('EURTRY=X'); if (eurtry) rates['EUR'] = eurtry.price;
    const gbptry = find('GBPTRY=X'); if (gbptry) rates['GBP'] = gbptry.price;
    const chftry = find('CHFTRY=X'); if (chftry) rates['CHF'] = chftry.price;

    if (from === 'TRY' && rates[to]) return 1 / rates[to];
    if (to === 'TRY' && rates[from]) return rates[from];
    if (rates[from] && rates[to]) return rates[from] / rates[to];
    return null;
  };

  const rate = getRate(fromCurrency, toCurrency);
  const convertedAmount = rate ? parseAmount(amount) * rate : null;
  const swap = () => { setFromCurrency(toCurrency); setToCurrency(fromCurrency); };

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="section-heading">Döviz Çevirici</h3>
        {!hideFullPageLink && (
          <Link to="/doviz-cevirici" className="text-xs font-medium text-accent-400 hover:text-accent-300 transition-colors">
            Tam sayfa →
          </Link>
        )}
      </div>
      <div className="space-y-3">
        <div>
          <label className="text-[11px] font-medium text-gray-500 mb-1 block">Miktar</label>
          <input
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount((prev) => sanitizeAmount(e.target.value, prev))}
            className="input-dark text-num"
            placeholder="0"
          />
        </div>
        <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-end">
          <div>
            <label className="text-[11px] font-medium text-gray-500 mb-1 block">Kaynak</label>
            <select value={fromCurrency} onChange={(e) => setFromCurrency(e.target.value)} className="input-dark text-num">
              {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} - {c.name}</option>)}
            </select>
          </div>
          <button
            onClick={swap}
            className="mb-1 p-2 rounded-lg text-accent-400 hover:text-accent-300 hover:bg-white/[0.05] transition-colors"
            title="Değiştir"
          >
            <ArrowLeftRight className="w-4 h-4" />
          </button>
          <div>
            <label className="text-[11px] font-medium text-gray-500 mb-1 block">Hedef</label>
            <select value={toCurrency} onChange={(e) => setToCurrency(e.target.value)} className="input-dark text-num">
              {CURRENCIES.map(c => <option key={c.code} value={c.code}>{c.code} - {c.name}</option>)}
            </select>
          </div>
        </div>
        {convertedAmount !== null ? (
          <div className="rounded-lg p-3 text-center bg-canvas border border-white/[0.06]">
            <p className="text-[11px] text-gray-500">{amount} {fromCurrency} =</p>
            <p className="text-num text-xl font-semibold text-up-400 mt-0.5">
              {convertedAmount.toLocaleString('tr-TR', { maximumFractionDigits: 4 })}
              <span className="text-xs text-gray-500 ml-1.5">{toCurrency}</span>
            </p>
            <p className="text-num text-[11px] text-gray-600 mt-1">1 {fromCurrency} = {rate?.toFixed(4)} {toCurrency}</p>
          </div>
        ) : (
          <div className="rounded-lg p-3 text-center bg-canvas border border-white/[0.06] text-gray-600 text-sm">
            Bu çevrim için veri yok
          </div>
        )}
      </div>
    </div>
  );
};

export default CurrencyConverter;
